import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import VolumeHighSvgIcon from "../../assets/ReactSvg/VolumeHighSvgIcon";
import VolumeSlashSvgIcon from "../../assets/ReactSvg/VolumeSlashSvgIcon";
import BackTransparentSvgIcon from "../../assets/ReactSvg/BackTransparentSvgIcon";
import useVideoMuted from "../../hooks/useVideoMuted";

const noop = () => {};

const LazyVideo = ({
  src,
  poster = "",
  className = "",
  onLoadedData = () => { },
}) => {
  const onLoadedDataRef = useRef(onLoadedData);
  onLoadedDataRef.current = onLoadedData;

  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const zoomedVideoRef = useRef(null);

  const [loadVideo, setLoadVideo] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isMuted, setIsMuted] = useVideoMuted();

  const notifiedSrc = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoadVideo(true);
        } else {
          if (videoRef.current) {
            videoRef.current.pause();
          }
        }
      },
      {
        rootMargin: "500px",
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setVideoLoaded(false);
    if (notifiedSrc.current !== src) {
      notifiedSrc.current = src;
      onLoadedDataRef.current();
    }
  }, [src]);

  useEffect(() => {
  if (zoomedVideoRef.current) zoomedVideoRef.current.muted = isMuted;
}, [isMuted, isZoomed]);

  // Lock background scroll while the zoom overlay is open (prevents iOS
  // rubber-band scroll of the page underneath the fixed overlay).
  useEffect(() => {
    if (isZoomed) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
  }, [isZoomed]);

  const handleCanPlay = () => {
    setVideoLoaded(true);
    // const video = videoRef.current;
    // video.play().catch(() => { });
  };

  const handleLoadedData = () => {
    setVideoLoaded(true);
    if (!poster && videoRef.current) {
      videoRef.current.currentTime = 0.1;
    }
  };

  const handleZoomIn = () => {
    setIsZoomed(true);
  };

  const handleZoomOut = () => {
    setIsZoomed(false);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  return (
    <>
      <div
        ref={containerRef}
        className="hostDineinMediaInner"
      // style={{
      //   position: "relative",
      //   overflow: "hidden",
      // }}
      >
        {loadVideo ? (
          <>
            {!videoLoaded && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "linear-gradient(90deg, #e0e0e0 25%, #ececec 37%, #e0e0e0 63%)",
                  backgroundSize: "400% 100%",
                  animation: "videoShimmer 1.4s ease-in-out infinite",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: "rgba(255, 255, 255, 0.6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      width: 0,
                      height: 0,
                      borderTop: "10px solid transparent",
                      borderBottom: "10px solid transparent",
                      borderLeft: "16px solid rgba(160,160,160,0.9)",
                      marginLeft: 4,
                    }}
                  />
                </div>

                <style>
                  {`
        @keyframes videoShimmer {
          0% { background-position: 100% 0; }
          100% { background-position: -100% 0; }
        }
      `}
                </style>
              </div>
            )}
            <video
              key={src}
              ref={videoRef}
              preload="auto"
              className={className}
              playsInline
              muted
              poster={poster}
              onCanPlay={handleCanPlay}
              onLoadedData={handleLoadedData}
              onClick={handleZoomIn}
            >
              <source src={src} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </>
        ) : (
          <div
            className={className}
            onClick={handleZoomIn}
            style={{
              width: "100%",
              height: "100%",
              background: "#f2f2f2",
              cursor: "pointer",
            }}
          >
            {poster && (
              <img
                src={poster}
                alt=""
                loading="lazy"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            )}
          </div>
        )}
      </div>

      {isZoomed &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              height: "100vh", // fallback for older browsers
              width: "100vw",
              background: "#000",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              paddingTop: "env(safe-area-inset-top, 0px)",
              paddingBottom: "env(safe-area-inset-bottom, 0px)",
            }}
            onClick={handleZoomOut}
          >
            <style>
              {`
                .lazyVideoZoomOverlay {
                  height: 100vh;
                  height: 100dvh; /* iOS-correct dynamic viewport height, wins when supported */
                }
              `}
            </style>
            <div
              className="lazyVideoZoomOverlay"
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <video
                ref={zoomedVideoRef}
                src={src}
                autoPlay
                playsInline
                muted={isMuted}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                handleZoomOut();
              }}
              style={{
                position: "absolute",
                top: "calc(16px + env(safe-area-inset-top, 0px))",
                left: "16px",
                ...buttonStyle,
              }}
              title="Zoom Out"
            >
              <BackTransparentSvgIcon />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleMute();
              }}
              style={{
                position: "absolute",
                top: "calc(16px + env(safe-area-inset-top, 0px))",
                right: "16px",
                ...buttonStyle,
              }}
              title={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? <VolumeSlashSvgIcon /> :  <VolumeHighSvgIcon />}
            </button>
          </div>,
          document.body
        )}
    </>
  );
};

const buttonStyle = {
  border: "none",
  borderRadius: "4px",
  background: "transparent",
  color: "#fff",
  // padding: "6px 10px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "bold",
};

export default LazyVideo;
